/**
 * Twinnee Director client — one ADK session shared by the dashboard planning
 * chat and the editor Movie Director chat.
 *
 * - createDirectorSession / resume: unified session id persisted on the project
 * - directorPlanningChat: dashboard (planning) surface -> /api/director/chat
 * - subscribeDirectorEvents: live SSE stream of tool calls + Grafana findings
 * - fetchDirectorMetrics / fetchDirectorStatus: production telemetry panel
 */
import api from '@/lib/api';

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/** Create or resume a Director session for a project/surface. */
export async function createDirectorSession({ projectId, surface = 'dashboard', editorMode = 'story', sessionId } = {}) {
  const { data } = await api.post('/director/sessions', {
    project_id: projectId,
    surface,
    editor_mode: editorMode,
    session_id: sessionId,
  });
  return data; // { session_id, surface, project_id }
}

/** Planning chat (dashboard surface). Returns { message, tools_used, handoff?, job_id?, session_id }. */
export async function directorPlanningChat({ message, projectId, sessionId, editorMode = 'story', context } = {}) {
  const { data } = await api.post('/director/chat', {
    message,
    project_id: projectId,
    surface: 'dashboard',
    editor_mode: editorMode,
    session_id: sessionId,
    context: context || {},
  });
  // Planner may surface a handoff via the last tool result; normalize it.
  return data;
}

/** Production telemetry snapshot for the health strip. */
export async function fetchDirectorMetrics() {
  const { data } = await api.get('/director/metrics');
  return data; // { snapshot, recent_events }
}

export async function fetchDirectorStatus() {
  const { data } = await api.get('/director/status');
  return data;
}

/** Seed the reproducible Wave-1 cinematic demo scenario into telemetry. */
export async function seedDirectorDemo() {
  const { data } = await api.post('/director/demo/seed', {});
  return data;
}

/** Apply the post-strategy improvement for the demo. */
export async function recoverDirectorDemo() {
  const { data } = await api.post('/director/demo/recover', {});
  return data;
}

/** Demo controls are shown only when REACT_APP_DIRECTOR_DEMO=1. */
export const DIRECTOR_DEMO_ENABLED = process.env.REACT_APP_DIRECTOR_DEMO === '1';

/**
 * Subscribe to a session's live event stream (tool calls, Grafana findings,
 * strategy changes). Returns an unsubscribe function.
 */
export function subscribeDirectorEvents(sessionId, onEvent) {
  if (!sessionId || typeof EventSource === 'undefined') return () => {};
  const origin = BACKEND_ORIGIN.replace(/\/$/, '');
  const base = origin.endsWith('/api') ? origin : `${origin}/api`;
  const url = `${base}/director/sessions/${encodeURIComponent(sessionId)}/events`;
  let es;
  try {
    es = new EventSource(url);
  } catch {
    return () => {};
  }
  es.onmessage = (e) => {
    if (!e?.data) return;
    try {
      onEvent(JSON.parse(e.data));
    } catch {
      /* ignore keep-alives / malformed */
    }
  };
  es.onerror = () => {
    /* browser auto-reconnects; swallow */
  };
  return () => {
    try {
      es.close();
    } catch {
      /* ignore */
    }
  };
}

/** Human-readable one-liner for a streamed Director event (for the trace log). */
export function describeDirectorEvent(evt) {
  if (!evt || !evt.type) return '';
  const tool = evt.tool ? ` ${evt.tool}` : '';
  switch (evt.type) {
    case 'tool_call':
      return `→ calling${tool}`;
    case 'tool_result':
      return `✓ ${evt.tool || 'tool'} returned`;
    case 'autopilot_observe':
      return `👁 Observing production (Grafana) for job ${evt.job_id || ''}`;
    case 'autopilot_reason':
      return `🧠 Diagnosis: ${evt?.strategy?.reason || 'unknown'} → ${evt?.strategy?.action || ''}`;
    case 'autopilot_act':
      return `🔧 Retrying with ${evt?.strategy?.provider || 'fallback'}`;
    default:
      return '';
  }
}

/** Map a tool name to a short family label + icon hint for the trace UI. */
export function toolFamily(tool) {
  const ops = ['observe_production', 'query_prometheus', 'query_loki_logs', 'tempo_get_trace', 'list_alerts', 'open_grafana_dashboard'];
  const qa = ['validate_shot', 'verify_improvement'];
  const strategy = ['diagnose_and_plan', 'retry_with_strategy'];
  if (ops.includes(tool)) return 'grafana';
  if (qa.includes(tool)) return 'qa';
  if (strategy.includes(tool)) return 'strategy';
  return 'production';
}
